import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrButton from '../../common/arr-button/ArrButton';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrModal from '../../common/arr-modal/ArrModal';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ISelectItem} from '../../../shared/types';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import {
  useAddSecurityGroup,
  useSecurityGroups,
  useUpdateSecurityGroup,
  useDeleteSecurityGroup,
} from '../../../hooks/securityGroup/useSecurityGroups';
import './SecurityGroupHeader.scss';
import {useQueryClient} from '@tanstack/react-query';

interface SecuritygroupHeaderProps {
  selectedGroupID: ISelectItem | null;
  onGroupChange: (group: string) => void;
  onGroupCreated?: () => void;
}

interface ApiSecurityGroup {
  key: number;
  value: string;
}

type ModalType = 'create' | 'rename' | 'delete' | null;

interface ToastState {
  id: string;
  title: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
}

const NO_GROUP_PLACEHOLDER: ISelectItem = {id: '', name: 'No Groups'};

const SecuritygroupHeader: React.FC<SecuritygroupHeaderProps> = ({
  onGroupChange,
  onGroupCreated,
}) => {
  const queryClient = useQueryClient();
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [newSecurityGroupName, setNewSecurityGroupName] = useState('');
  const [renameGroupName, setRenameGroupName] = useState('');
  const [toasts, setToasts] = useState<ToastState[]>([]);
  const {data, isLoading, isError} = useSecurityGroups();
  const DUPLICATE_NAME_MESSAGE =
    'A security group with the same name already exists in the system. Please provide a different security group name.';

  const INVALID_NAME_REGEX = /[<>'&]/;
  const INVALID_NAME_MESSAGE =
    "Security group names cannot contain: <, >, ', and &.";

  const [createNameError, setCreateNameError] = useState('');
  const [renameNameError, setRenameNameError] = useState('');
  const [addedSecurityGroupName, setAddedSecurityGroupName] = useState('');
  const addToast = useCallback((toast: Omit<ToastState, 'id'>) => {
    const newToast: ToastState = {
      ...toast,
      id: Date.now().toString(),
    };
    setToasts((prev) => [...prev, newToast]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Map and sort API results to dropdown items
  const securityGroups: ISelectItem[] = useMemo(() => {
    const apiList: ApiSecurityGroup[] = data?.securityGroupList ?? [];
    return apiList
      .map((g) => ({id: String(g.key), name: g.value}))
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {sensitivity: 'base'})
      );
  }, [data]);

  const existingNameSet = useMemo(() => {
    return new Set(
      securityGroups.map((g) => (g.name || '').toLowerCase().trim())
    );
  }, [securityGroups]);

  // Local selected group state
  const [selectedGroup, setSelectedGroup] = useState<ISelectItem>(() => ({
    ...NO_GROUP_PLACEHOLDER,
    name: t('securityGroup.noGroups'),
  }));

  // Keep selectedGroup synchronized with server list
  useEffect(() => {
    // If there are no groups, show translated placeholder
    if (!securityGroups.length) {
      setSelectedGroup({
        ...NO_GROUP_PLACEHOLDER,
        name: t(''),
      });
      setRenameGroupName('');
      return;
    }

    if (addedSecurityGroupName) {
      const found = securityGroups.find(
        (g) => g.name === addedSecurityGroupName
      );
      if (found) {
        setSelectedGroup(found);
        onGroupChange(found.id);
      }
      setAddedSecurityGroupName('');
      return;
    }
    // If currently selected group's id exists in server list, preserve it (but update name if changed)
    const found = securityGroups.find((g) => g.id === selectedGroup.id);
    if (found) {
      if (found.name !== selectedGroup.name) {
        setSelectedGroup(found);
      }
      setRenameGroupName(found.name);
      return;
    }

    // If selected group no longer exists (deleted), pick first group
    const firstGroup = securityGroups[0];
    setSelectedGroup(firstGroup);
    setRenameGroupName(firstGroup.name);
    onGroupChange(firstGroup.id);
  }, [securityGroups]);

  // Selection handler
  const handleSelectGroup = useCallback((group: ISelectItem | null) => {
    if (!group) return;
    setSelectedGroup(group);
    setRenameGroupName(group.name);
    onGroupChange(group.id);
  }, []);

  // Modal open/close
  const handleOpenModal = useCallback(
    (type: ModalType) => {
      setCreateNameError('');
      setRenameNameError('');
      if (type === 'rename') {
        setRenameGroupName(selectedGroup.name || '');
      } else if (type === 'create') {
        setNewSecurityGroupName('');
      }
      setActiveModal(type);
    },
    [selectedGroup.name]
  );

  const handleCloseModal = useCallback(() => {
    setActiveModal(null);
    setNewSecurityGroupName('');
    setRenameGroupName(selectedGroup?.name ?? '');
    setCreateNameError('');
    setRenameNameError('');
  }, [selectedGroup?.name]);

  // Mutations
  const {mutate: mutateAdd} = useAddSecurityGroup();
  const {mutate: mutateUpdate} = useUpdateSecurityGroup();
  const {mutate: mutateDelete} = useDeleteSecurityGroup();

  const safeErrorMessage = (error: any, fallback: string) =>
    (error && (error.message || error?.response?.data?.message)) || fallback;

  const handleCreateNew = useCallback(() => {
    const name = newSecurityGroupName.trim();
    if (!name) return;

    const normalized = name.toLowerCase().trim();
    if (INVALID_NAME_REGEX.test(name)) {
      setCreateNameError(INVALID_NAME_MESSAGE);
      return;
    }
    if (existingNameSet.has(normalized)) {
      setCreateNameError(DUPLICATE_NAME_MESSAGE);
      return;
    }

    mutateAdd(
      {securityGroupName: name},
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['securityGroups']);
          addToast({
            title: t('securityGroup.toastMessages.successTitle'),
            message: t('securityGroup.toastMessages.createNewSuccess'),
            type: 'success',
          });
          setAddedSecurityGroupName(name);
          handleCloseModal();
          if (onGroupCreated) {
            onGroupCreated();
          }
        },
        onError: (error: any) => {
          addToast({
            title: t('securityGroup.toastMessages.errorTitle'),
            message: safeErrorMessage(
              error,
              'Failed to create security group.'
            ),
            type: 'error',
          });
          handleCloseModal();
        },
      }
    );
  }, [
    onGroupCreated,
    mutateAdd,
    newSecurityGroupName,
    existingNameSet,
    handleCloseModal,
    addToast,
    queryClient,
  ]);

  const handleSaveRename = useCallback(() => {
    if (!selectedGroup?.id) return;
    const trimmed = renameGroupName.trim();
    if (trimmed === '' || trimmed === selectedGroup.name) {
      handleCloseModal();
      return;
    }

    if (INVALID_NAME_REGEX.test(trimmed)) {
      setRenameNameError(INVALID_NAME_MESSAGE);
      return;
    }

    const normalized = trimmed.toLowerCase().trim();
    const selectedNormalized = (selectedGroup?.name || '').toLowerCase().trim();

    if (normalized !== selectedNormalized && existingNameSet.has(normalized)) {
      setRenameNameError(DUPLICATE_NAME_MESSAGE);
      return;
    }

    mutateUpdate(
      {
        securityGroupID: Number(selectedGroup.id),
        securityGroupName: trimmed,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['securityGroups']);
          addToast({
            title: t('securityGroup.toastMessages.successTitle'),
            message: t('securityGroup.toastMessages.renameSuccess'),
            type: 'success',
          });
          handleCloseModal();
        },
        onError: (error: any) => {
          addToast({
            title: t('securityGroup.toastMessages.errorTitle'),
            message: safeErrorMessage(
              error,
              'Failed to rename security group.'
            ),
            type: 'error',
          });
          handleCloseModal();
        },
      }
    );
  }, [
    mutateUpdate,
    renameGroupName,
    selectedGroup?.id,
    selectedGroup?.name,
    existingNameSet,
    handleCloseModal,
    addToast,
    queryClient,
  ]);

  const handleDeleteConfirm = useCallback(() => {
    if (!selectedGroup?.id) return;

    mutateDelete(Number(selectedGroup.id), {
      onSuccess: () => {
        queryClient.invalidateQueries(['securityGroups']);
        addToast({
          title: t('securityGroup.toastMessages.successTitle'),
          message: t('securityGroup.toastMessages.deleteSuccess'),
          type: 'success',
        });
        handleCloseModal();
      },
      onError: (error: any) => {
        addToast({
          title: t('securityGroup.toastMessages.errorTitle'),
          message: safeErrorMessage(error, 'Failed to delete security group.'),
          type: 'error',
        });
        handleCloseModal();
      },
    });
  }, [
    mutateDelete,
    selectedGroup?.id,
    handleCloseModal,
    addToast,
    queryClient,
  ]);

  // Modal button configs memoized to avoid recreating on each render
  const createModalButtons = useMemo(
    () => [
      {
        id: 'cancel',
        label: t('common.cancel'),
        onClick: handleCloseModal,
        variant: 'secondary' as const,
        dataTestId: 'create-cancel',
        ariaLabel: t('common.cancel'),
        ariaDescribedBy: t('securityGroup.modal.cancelButton'),
      },
      {
        id: 'create',
        label: t('securityGroup.createNewButton.title'),
        onClick: handleCreateNew,
        variant: 'primary' as const,
        dataTestId: 'create-new',
        disabled: newSecurityGroupName.trim() === '' || !!createNameError,
        ariaLabel: t('securityGroup.createNewButton.title'),
        ariaDescribedBy: t('securityGroup.modal.createnewButton'),
      },
    ],
    [handleCloseModal, handleCreateNew, newSecurityGroupName]
  );

  const renameModalButtons = useMemo(
    () => [
      {
        id: 'cancel',
        label: t('common.cancel'),
        onClick: handleCloseModal,
        variant: 'secondary' as const,
        dataTestId: 'rename-cancel',
        ariaLabel: t('common.cancel'),
        ariaDescribedBy: t('securityGroup.modal.cancelButton'),
      },
      {
        id: 'save',
        label: t('common.save'),
        onClick: handleSaveRename,
        variant: 'primary' as const,
        dataTestId: 'rename-save',
        disabled:
          renameGroupName.trim() === '' ||
          renameGroupName.trim() === (selectedGroup?.name ?? '') ||
          !!renameNameError,
        ariaLabel: t('common.save'),
        ariaDescribedBy: t('securityGroup.modal.renameSaveButton'),
      },
    ],
    [handleCloseModal, handleSaveRename, renameGroupName, selectedGroup?.name]
  );

  const deleteModalButtons = useMemo(
    () => [
      {
        id: 'cancel-delete',
        label: t('common.cancel'),
        onClick: handleCloseModal,
        variant: 'secondary' as const,
        dataTestId: 'delete-cancel',
        ariaLabel: t('common.cancel'),
        ariaDescribedBy: t('securityGroup.modal.cancelButton'),
      },
      {
        id: 'confirm-delete',
        label: t('securityGroup.deleteButton.confirmDelete'),
        onClick: handleDeleteConfirm,
        variant: 'primary' as const,
        dataTestId: 'delete-confirm',
        disabled: !selectedGroup?.id,
        ariaLabel: t('securityGroup.deleteButton.confirmDelete'),
        ariaDescribedBy: t('securityGroup.modal.deleteButton'),
      },
    ],
    [handleCloseModal, handleDeleteConfirm, selectedGroup?.id]
  );

  // Whether controls that operate on a selected group should be disabled
  const selectionDisabled = !selectedGroup?.id || isLoading || isError;

  return (
    <Box className="view-securitygroup-header">
      <Box className="view-securitygroup-title">
        <Typography
          className="typo-text headline3_size view-securitygroup-title-text"
          tabIndex={0}
          data-testid={`security-group-header-title`}
        >
          {t('securityGroup.title')}
        </Typography>

        <ArrTooltipWrapper
          title={t('securityGroup.headerTooltip.title')}
          trigger={
            <div tabIndex={0} data-testid={`security-group-header-div`}>
              <InfoIcon
                fontSize="medium"
                className="view-user-info-icon"
                data-testid="view-user-info-icon"
              />
            </div>
          }
        >
          <p data-testid={`security-group-header-content`}>
            {t('securityGroup.headerTooltip.content')}
          </p>
          <p data-testid={`security-group-header-content1`}>
            {t('securityGroup.headerTooltip.content1')}
          </p>
        </ArrTooltipWrapper>
      </Box>
      <Box className="view-securitygroup-header-filter">
        <ArrSingleSelectDropdown
          dropdownLabel={t('common.sortby')}
          sizeVariant="medium"
          dropdownItems={securityGroups}
          dropdownItemKey="id"
          selectedValues={selectedGroup}
          onSelectionChange={handleSelectGroup}
          customWidth={18}
          disabled={isLoading}
          searchable
          showNoResultsText
          dataTestId="sortby-dropdown"
          aria-label={t('securityGroup.securityGroupDropdown.hintTitle')}
          aria-live="polite"
          aria-atomic="true"
        />
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <ArrButton
            type="button"
            variant="secondary"
            title={t('securityGroup.createNewButton.buttonTitle')}
            size="small"
            onClick={() => handleOpenModal('create')}
            disabled={isLoading}
            dataTestId="create-button"
            aria-label={t('common.create')}
            aria-describedby={t('securityGroup.createNewButton.buttonTitle')}
            aria-live="polite"
            aria-atomic="false"
          >
            {t('common.create')}
          </ArrButton>

          <ArrButton
            variant="secondary"
            size="small"
            onClick={() => handleOpenModal('rename')}
            disabled={selectionDisabled}
            title={t('securityGroup.renameButton.buttonTitle')}
            dataTestId="rename-button"
            aria-label={t('common.rename')}
            aria-describedby={t('securityGroup.renameButton.buttonTitle')}
            aria-live="polite"
            aria-atomic="false"
          >
            {t('common.rename')}
          </ArrButton>

          <ArrButton
            variant="secondary"
            size="small"
            onClick={() => handleOpenModal('delete')}
            disabled={selectionDisabled}
            title={t('securityGroup.deleteButton.buttonTitle')}
            dataTestId="delete-button"
            aria-label={t('common.delete')}
            aria-describedby={t('securityGroup.deleteButton.buttonTitle')}
            aria-live="polite"
            aria-atomic="false"
          >
            {t('common.delete')}
          </ArrButton>
        </Box>
      </Box>
      {/* Create Modal */}
      <ArrModal
        showModal={activeModal === 'create'}
        title={t('securityGroup.createNewButton.headerTitle')}
        showClose
        autoFocusCloseButton={true}
        onClose={handleCloseModal}
        buttons={createModalButtons}
        dataTestId="Create-security-group-modal"
        className="Create-security-group-modal"
        ariaLabel={t('securityGroup.createNewButton.headerTitle')}
        ariaDescription={t('securityGroup.createNewButton.headerTitle')}
      >
        <ArrTextField
          label={t('securityGroup.createNewButton.textfieldLabel')}
          fullWidth
          value={newSecurityGroupName}
          onChange={(e) => {
            const value = e.target.value;
            setNewSecurityGroupName(value);

            const normalized = value.toLowerCase().trim();

            if (INVALID_NAME_REGEX.test(value)) {
              setCreateNameError(INVALID_NAME_MESSAGE);
              return;
            }

            // If normalized exists in set -> show duplicate
            if (normalized && existingNameSet.has(normalized)) {
              setCreateNameError(DUPLICATE_NAME_MESSAGE);
            } else {
              setCreateNameError('');
            }
          }}
          state={createNameError ? 'error' : 'enabled'}
          supportText={createNameError || ''}
          dataTestId="security-group-create-input"
          autoFocus
        />
      </ArrModal>
      {/* Rename Modal */}
      <ArrModal
        showModal={activeModal === 'rename'}
        title={t('securityGroup.renameButton.headerTitle')}
        showClose
        autoFocusCloseButton={true}
        onClose={handleCloseModal}
        buttons={renameModalButtons}
        dataTestId="rename-security-group-modal"
        className="rename-security-group-modal"
        ariaLabel={t('securityGroup.renameButton.headerTitle')}
        ariaDescription={t('securityGroup.renameButton.headerTitle')}
      >
        <ArrTextField
          label={t('securityGroup.renameButton.textfieldLabel')}
          fullWidth
          value={renameGroupName}
          onChange={(e) => {
            const value = e.target.value;
            setRenameGroupName(value);

            const normalized = value.toLowerCase().trim();
            // invalid chars
            if (INVALID_NAME_REGEX.test(value)) {
              setRenameNameError(INVALID_NAME_MESSAGE);
              return;
            }

            // if normalized equals selectedGroup name, it's NOT a duplicate
            const selectedNormalized = (selectedGroup?.name || '')
              .toLowerCase()
              .trim();
            if (
              normalized &&
              normalized !== selectedNormalized &&
              existingNameSet.has(normalized)
            ) {
              setRenameNameError(DUPLICATE_NAME_MESSAGE);
            } else {
              setRenameNameError('');
            }
          }}
          state={renameNameError ? 'error' : 'enabled'}
          supportText={renameNameError || ''}
          dataTestId="security-group-rename-input"
          autoFocus
        />
      </ArrModal>
      {/* Delete Modal */}
      <ArrModal
        showModal={activeModal === 'delete'}
        headerTitle={t('securityGroup.deleteButton.headerTitle')}
        title={t('securityGroup.deleteButton.confirmMsg')}
        showClose
        autoFocusCloseButton={true}
        onClose={handleCloseModal}
        buttons={deleteModalButtons}
        dataTestId="Delete-security-group-modal"
        className="Delete-security-group-modal"
        ariaLabel={t('securityGroup.deleteButton.confirmMsg')}
        ariaDescription={t('securityGroup.deleteButton.confirmMsg')}
      >
        <Typography
          variant="body2"
          tabIndex={0}
          className="typo-text desc-text-color"
          aria-label={t('securityGroup.deleteButton.securityGroup')}
          aria-describedby={t('securityGroup.deleteButton.securityGroup')}
          data-testid={`security-group-header-security-group`}
        >
          {t('securityGroup.deleteButton.securityGroup')}{' '}
          <span
            tabIndex={0}
            className="securitygroup-name"
            aria-label={selectedGroup?.name + ''}
            aria-describedby={selectedGroup?.name + ''}
            data-testid={`security-group-selected-grup-name`}
          >
            {selectedGroup?.name}
          </span>
        </Typography>
      </ArrModal>
      {toasts.map((toast) => (
        <ArrToastNotification
          key={toast.id}
          message={toast.message}
          status={[toast.type as ToastType]}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </Box>
  );
};

export default SecuritygroupHeader;
